import {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {NetworkContext} from '../lib/context';
import ProviderLocationsTable from '../components/provider-location-table';
// import MemberLocationsTable from '../components/member-location-table';
import CustomTabs from '../components/tabs/tabs';
import {ToastNotification} from '@carbon/react';
import LoadingModal from '../components/loading-modal';
import './myFiles.scss';

const MyFiles = observer(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, setError] = useState<string | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    
    const networkOptimizationStore = useContext(NetworkContext);
    
    // Use store's loading state and data directly
    const loading = isInitialLoading || networkOptimizationStore.loadingProviderRosters;
    const rows = networkOptimizationStore.providerRosters;

    const handleCopyRoster = async (rosterId: number, revisionName: string, notes: string) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.copyRoster(rosterId, revisionName, notes, abortController);
        } catch (error) {
            console.error('Failed to copy roster:', error);
            throw error;
        }
    };

    const handleRenameRoster = async (rosterId: number, revisionId: number, revisionName: string, notes: string | null) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.renameRoster(rosterId, revisionId, revisionName, notes, abortController);
        } catch (error) {
            console.error('Failed to copy roster:', error);
            throw error;
        }
    };

    const handleDeleteRoster = async (rosterId: number, revisionId: number) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.deleteRoster(rosterId, revisionId, abortController);
        } catch (error) {
            console.error('Failed to copy roster:', error);
            throw error;
        }
    };

    useEffect(() => {
        const abortController = new AbortController();
        setIsInitialLoading(true);
        networkOptimizationStore.reset();

        const loadData = async () => {
            setError(null);
            try {
                // Use store's cached roster data instead of direct API call
                if (networkOptimizationStore.providerRosters.length === 0 && !networkOptimizationStore.loadingProviderRosters) {
                    await networkOptimizationStore.loadProviderRosters(abortController);
                }
                // NOTE: This section is commented out for now as patient rosters are not yet available.
                // if (networkOptimizationStore.patientRosters.length === 0 && !networkOptimizationStore.loadingPatientRosters) {
                //     await networkOptimizationStore.loadPatientRosters(abortController);
                // }

                // Check for existing SSE streams (e.g., roster copy operations) and reconnect if needed
                networkOptimizationStore.checkForExistingStreams();
            } catch (err) {
                setError('Failed to load My Files data. Please try again later.');
            }
            finally {
                setIsInitialLoading(false);
            }
        };

        loadData();
        return () => abortController.abort();
    }, [networkOptimizationStore]);

    return (
        <div className="min-h-[calc(100vh-11rem+2px)] w-full overflow-hidden myFiles">
            <div className="flex items-center justify-between rounded px-12 py-8 mb-4 bg-white">
                <div className="flex flex-col justify-center">
                    <h3 className="text-3xl text-[#222B36] font-normal m-0">My Files</h3>
                    <p className="text-base text-[#646A72] m-0">Choose to upload a new file or browse saved files</p>
                </div>
            </div>
            <div className="grid grid-cols-6 gap-4 px-12 py-4">
                <div className="col-span-6 rounded">
                    <div className="w-full py-4 flex">
                        <CustomTabs 
                            variant="boxed"
                            noPadding={true}
                            tabs={[
                                {
                                    label: 'Provider/Facility Locations',
                                    content: <ProviderLocationsTable 
                                        rows={rows} 
                                        loading={loading} 
                                        onCopyRoster={handleCopyRoster}
                                        onRenameRoster={handleRenameRoster}
                                        onDeleteRoster={handleDeleteRoster}
                                    />
                                },
                                // NOTE: This section is commented out for now as patient rosters are not yet available.
                                // {
                                //     label: 'Member Locations',
                                //     content: <MemberLocationsTable rows={patientRostersRows} loading={loadingPatientRosters} />
                                // }
                            ]}
                        />
                    </div>
                </div>
            </div>
            
            {/* Loading Modal */}
            <LoadingModal open={networkOptimizationStore.isRosterActionLoading} />
            
            {/* Toast Notification */}
            {networkOptimizationStore.showToast && (
                <ToastNotification
                    aria-label="closes notification"
                    caption=""
                    kind={networkOptimizationStore.toastKind}
                    onClose={() => networkOptimizationStore.hideToastNotification()}
                    onCloseButtonClick={() => networkOptimizationStore.hideToastNotification()}
                    role="alert"
                    statusIconDescription="notification"
                    subtitle={networkOptimizationStore.toastSubtitle}
                    timeout={5000}
                    title={networkOptimizationStore.toastTitle}
                    style={{
                        position: 'fixed',
                        top: '3rem',
                        right: '1rem',
                        zIndex: 9999,
                        minWidth: '350px',
                        maxWidth: '500px'
                    }}
                />
            )}
        </div>
    );
});

export default MyFiles;
